import { CircularProgress } from '@mui/material';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import UPInput from 'components/input';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { MinorBreakRule } from 'constants/MinorBreakRule';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__CONTINUOUS_BREAK,
  CONTRACT_COMPLIANCE__CONTINUOUS_EXCEEDS_TOTAL_HINT,
  CONTRACT_COMPLIANCE__CONTINUOUS_WORK_THRESHOLD,
  CONTRACT_COMPLIANCE__EDIT_DEFAULT_VALUE,
  CONTRACT_COMPLIANCE__LEGAL_WORK_TIME_THRESHOLD,
  CONTRACT_COMPLIANCE__MINIMUM_BREAK,
  CONTRACT_COMPLIANCE__MINUTES,
  CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT,
  CONTRACT_COMPLIANCE__TOTAL_BREAK,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import JustificationFields from '../JustificationFields';
import { HintText, PanelErrorRow, PanelFields } from '../styles';
import { RULE_TITLE_KEYS } from './constants';
import { UseRuleDefaultPanelResult } from '../hooks/useRuleDefaultPanel';
import { DefaultDraft } from './utils';

interface MinorBreakDefaultPanelProps {
  panel: UseRuleDefaultPanelResult<MinorBreakRule, DefaultDraft>;
}

const TEST_ID = 'minor-breaks-default';

/** Values are edited in whole minutes; the threshold also shows its h/min equivalent. */
const MinorBreakDefaultPanel = ({ panel }: MinorBreakDefaultPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const { rule, status, draft, fieldErrors, isSaving, loadErrorMessage, saveErrorMessage } = panel;
  const isLegalWorkTime = rule === MinorBreakRule.LEGAL_WORK_TIME;
  const isBusy = isSaving || panel.justification.isUploading;

  // UPInput type="number" reports a number, or null once cleared (emptyAsNull).
  const onMinutesChange = (field: keyof DefaultDraft) => (value: number | null) =>
    panel.setField(field, value === null ? '' : String(value));

  return (
    <UPSidePanel
      open={status !== 'closed'}
      title={rule ? t(RULE_TITLE_KEYS[rule]) : ''}
      // Two-line header like the feature access panel, so the card starts at the same height.
      subtitle={t(CONTRACT_COMPLIANCE__EDIT_DEFAULT_VALUE)}
      onClose={panel.close}
      testId={`${TEST_ID}-panel`}
      footer={
        <>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            onClick={panel.requestSave}
            disabled={status !== 'ready' || isBusy || !panel.isComplete}
            testId={`${TEST_ID}-save`}
          />
          <UPButton text={t(GENERAL__CLOSE)} onClick={panel.close} outlined testId={`${TEST_ID}-close`} />
        </>
      }
    >
      {status === 'loading' && <CircularProgress data-testid={`${TEST_ID}-panel-loading`} />}

      {status === 'loadError' && loadErrorMessage && (
        <PanelErrorRow data-testid={`${TEST_ID}-panel-load-error`}>
          <ErrorBar message={loadErrorMessage} />
          <UPButton text={t(GENERAL__RETRY)} onClick={panel.retryLoad} outlined testId={`${TEST_ID}-panel-retry`} />
        </PanelErrorRow>
      )}

      {saveErrorMessage && (
        <PanelErrorRow data-testid={`${TEST_ID}-save-error`}>
          <ErrorBar message={saveErrorMessage} />
        </PanelErrorRow>
      )}

      {status === 'ready' && rule && (
        <UPSidePanelSection title={t(CONTRACT_COMPLIANCE__EDIT_DEFAULT_VALUE)} testId={`${TEST_ID}-details`}>
          <>
            <PanelFields>
              <UPInput
                type="number"
                emptyAsNull
                min={1}
                step={1}
                unit={t(CONTRACT_COMPLIANCE__MINUTES)}
                label={t(
                  isLegalWorkTime
                    ? CONTRACT_COMPLIANCE__LEGAL_WORK_TIME_THRESHOLD
                    : CONTRACT_COMPLIANCE__CONTINUOUS_WORK_THRESHOLD,
                )}
                placeholder=""
                value={draft.threshold}
                onChange={onMinutesChange('threshold')}
                error={!!fieldErrors.threshold}
                helperText={fieldErrors.threshold ? t(fieldErrors.threshold) : ''}
                disabled={isSaving}
                required
                testId={`${TEST_ID}-threshold`}
              />
              <UPInput
                type="number"
                emptyAsNull
                min={0}
                step={1}
                unit={t(CONTRACT_COMPLIANCE__MINUTES)}
                label={t(isLegalWorkTime ? CONTRACT_COMPLIANCE__TOTAL_BREAK : CONTRACT_COMPLIANCE__MINIMUM_BREAK)}
                placeholder=""
                value={draft.minimumBreak}
                onChange={onMinutesChange('minimumBreak')}
                error={!!fieldErrors.minimumBreak}
                helperText={fieldErrors.minimumBreak ? t(fieldErrors.minimumBreak) : ''}
                disabled={isSaving}
                required
                testId={`${TEST_ID}-minimum-break`}
              />
              {isLegalWorkTime && (
                <UPInput
                  type="number"
                  emptyAsNull
                  min={0}
                  step={1}
                  unit={t(CONTRACT_COMPLIANCE__MINUTES)}
                  label={t(CONTRACT_COMPLIANCE__CONTINUOUS_BREAK)}
                  placeholder=""
                  value={draft.continuousBreak}
                  onChange={onMinutesChange('continuousBreak')}
                  error={!!fieldErrors.continuousBreak}
                  helperText={t(fieldErrors.continuousBreak ?? CONTRACT_COMPLIANCE__CONTINUOUS_EXCEEDS_TOTAL_HINT)}
                  disabled={isSaving}
                  required
                  testId={`${TEST_ID}-continuous-break`}
                />
              )}
            </PanelFields>
            <HintText>{t(CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT)}</HintText>
            <JustificationFields justification={panel.justification} disabled={isSaving} testIdPrefix={TEST_ID} />
          </>
        </UPSidePanelSection>
      )}
    </UPSidePanel>
  );
};

export default MinorBreakDefaultPanel;
